import { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ClockCounterClockwise, MagnifyingGlass, Trash, ArrowsClockwise, SlidersHorizontal } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth, canEditIt } from '@/auth/AuthContext';
import { itApi } from './itApi';
import { DISPOSAL_REASONS, DISPOSAL_METHODS } from './DisposalDialog';
import { disposalCols } from './disposalColumns';
import { useTableColumns } from '@/lib/useTableColumns';
import TableColumnsDialog from '@/components/table/TableColumnsDialog';

const ALL = '__all__';

const REASON_COLOR = {
  broken: 'bg-rose-50 text-rose-700 border-rose-200',
  obsolete: 'bg-amber-50 text-amber-700 border-amber-200',
  lost: 'bg-orange-50 text-orange-700 border-orange-200',
  stolen: 'bg-red-100 text-red-800 border-red-300',
  expired: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  other: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

/**
 * 廃棄履歴 (Disposal history) — dedicated log of every 廃棄 event.
 * Filters: date range / reason / method / asset_type / free-text search.
 * Backed by `it_disposals` collection populated by POST /assets/{id}/dispose.
 */
export default function DisposalHistoryPage() {
  const { user } = useAuth();
  // Route guard — 廃棄履歴 is admin/it_edit only (matches backend gating on GET /api/it/disposals).
  // The sidebar link is already hidden for non-admins; this covers direct URL access.
  if (!canEditIt(user)) return <Navigate to="/it" replace />;
  return <DisposalHistory />;
}

function DisposalHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState('');
  const [assetType, setAssetType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [colsDialogOpen, setColsDialogOpen] = useState(false);
  const cols = useTableColumns(disposalCols);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (reason) params.reason = reason;
      if (method) params.method = method;
      if (assetType) params.asset_type = assetType;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const data = await itApi.disposals(params);
      setRows(data);
    } catch (_e) {
      toast.error('廃棄履歴の取得に失敗しました');
    } finally { setLoading(false); }
  }, [q, reason, method, assetType, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const totalQty = rows.filter((r) => r.qty).reduce((s, r) => s + Number(r.qty || 0), 0);
  const consumableCount = rows.filter((r) => r.asset_type === 'consumable').length;
  const durableCount = rows.filter((r) => (r.asset_type || 'durable') === 'durable').length;

  return (
    <div className="space-y-5 page-enter" data-testid="disposal-history">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <ClockCounterClockwise size={26} weight="duotone" className="text-rose-500" />
            廃棄履歴
          </h1>
          <p className="text-sm text-zinc-500 mt-1">資産・消耗品の廃棄記録を検索できます。</p>
        </div>
        <Button variant="outline" onClick={load} className="h-10" data-testid="disp-refresh">
          <ArrowsClockwise size={16} className="mr-1.5" weight="bold" /> 再読込
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3">
          <p className="text-[10px] font-semibold uppercase text-zinc-500">件数</p>
          <p className="text-xl font-bold text-zinc-900 tabular-nums mt-0.5" data-testid="disp-count">{rows.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3">
          <p className="text-[10px] font-semibold uppercase text-zinc-500">資産</p>
          <p className="text-xl font-bold text-zinc-900 tabular-nums mt-0.5">{durableCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3">
          <p className="text-[10px] font-semibold uppercase text-zinc-500">消耗品</p>
          <p className="text-xl font-bold text-zinc-900 tabular-nums mt-0.5">{consumableCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3">
          <p className="text-[10px] font-semibold uppercase text-zinc-500">消耗品 廃棄数量合計</p>
          <p className="text-xl font-bold text-zinc-900 tabular-nums mt-0.5">{totalQty.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3 flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="disp-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="品名・タグ・カテゴリで検索" className="h-10 pl-9" />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase text-zinc-500 block mb-1">開始日</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 w-40" data-testid="disp-from" />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase text-zinc-500 block mb-1">終了日</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 w-40" data-testid="disp-to" />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase text-zinc-500 block mb-1">理由</label>
          <Select value={reason || ALL} onValueChange={(v) => setReason(v === ALL ? '' : v)}>
            <SelectTrigger data-testid="disp-reason" className="h-10 w-36"><SelectValue placeholder="すべて" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>すべて</SelectItem>
              {DISPOSAL_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase text-zinc-500 block mb-1">方法</label>
          <Select value={method || ALL} onValueChange={(v) => setMethod(v === ALL ? '' : v)}>
            <SelectTrigger data-testid="disp-method" className="h-10 w-36"><SelectValue placeholder="すべて" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>すべて</SelectItem>
              {DISPOSAL_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase text-zinc-500 block mb-1">種類</label>
          <Select value={assetType || ALL} onValueChange={(v) => setAssetType(v === ALL ? '' : v)}>
            <SelectTrigger data-testid="disp-type" className="h-10 w-32"><SelectValue placeholder="すべて" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>すべて</SelectItem>
              <SelectItem value="durable">資産</SelectItem>
              <SelectItem value="consumable">消耗品</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="outline" onClick={() => setColsDialogOpen(true)}
          data-testid="disp-cols-btn" title="列の表示・並び替え"
          className="h-10 ml-auto border-zinc-200 text-zinc-600 hover:text-zinc-800">
          <SlidersHorizontal size={14} weight="bold" className="mr-1.5" /> 列設定
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          {(() => {
            const COLUMN_MAP = {
              disposal_date: { key: 'disposal_date', header: '廃棄日', thCls: 'w-28',
                cell: (r) => <span className="whitespace-nowrap font-mono text-xs text-zinc-500">{r.disposal_date}</span> },
              asset_type: { key: 'asset_type', header: '種類', thCls: 'w-20',
                cell: (r) => r.asset_type === 'consumable'
                  ? <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] whitespace-nowrap">消耗品</Badge>
                  : <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[10px] whitespace-nowrap">資産</Badge> },
              asset_name: { key: 'asset_name', header: '資産名',
                cell: (r) => (
                  <>
                    <div className="text-zinc-800 font-medium"><span data-no-translate>{r.asset_name}</span></div>
                    {r.disposal_notes && <div className="text-xs text-zinc-500 truncate max-w-xs">{r.disposal_notes}</div>}
                  </>
                ) },
              asset_tag: { key: 'asset_tag', header: 'タグ', thCls: 'w-32',
                cell: (r) => <span className="font-mono text-xs text-zinc-500">{r.asset_tag || '—'}</span> },
              category: { key: 'category', header: 'カテゴリ', thCls: 'w-40',
                cell: (r) => <span className="text-zinc-600 whitespace-nowrap">{r.category || '—'}</span> },
              qty: { key: 'qty', header: '数量', thCls: 'text-right w-24', tdCls: 'text-right',
                cell: (r) => r.qty != null
                  ? <span className="tabular-nums text-zinc-700">{r.qty} {r.unit || ''}</span>
                  : <span className="text-zinc-300">—</span> },
              disposal_reason: { key: 'disposal_reason', header: '理由', thCls: 'w-28',
                cell: (r) => (
                  <Badge variant="outline" className={`${REASON_COLOR[r.disposal_reason] || REASON_COLOR.other} text-[10px]`}>
                    <Trash size={10} weight="bold" className="mr-1" />
                    {r.disposal_reason_label || r.disposal_reason}
                  </Badge>
                ) },
              disposal_method: { key: 'disposal_method', header: '方法', thCls: 'w-28',
                cell: (r) => <span className="text-zinc-600 text-xs">{r.disposal_method_label || '—'}</span> },
              disposed_by: { key: 'disposed_by', header: '実施者', thCls: 'w-32',
                cell: (r) => <span className="text-zinc-600 text-xs truncate max-w-[120px] block">{r.disposed_by_name || '—'}</span> },
            };
            const visibleCols = cols.applyTo(COLUMN_MAP);
            return (
              <table className="w-full min-w-[860px] text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200 text-left text-xs font-semibold text-zinc-600">
                    {visibleCols.map((c) => (
                      <th key={c.key} className={`px-3 py-2.5 whitespace-nowrap ${c.thCls || ''}`}>{c.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="disp-table">
                  {loading && rows.length === 0 ? (
                    <tr><td colSpan={visibleCols.length} className="px-4 py-10 text-center text-sm text-zinc-400">読込中...</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={visibleCols.length} className="px-4 py-10 text-center text-sm text-zinc-400">廃棄履歴はありません</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.id} className="border-b border-zinc-200 hover:bg-zinc-50/50" data-testid={`disp-row-${r.id}`}>
                      {visibleCols.map((c) => (
                        <td key={c.key} className={`px-3 py-2.5 ${c.tdCls || ''}`}>{c.cell(r)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </div>
      </div>
      <TableColumnsDialog open={colsDialogOpen} onClose={() => setColsDialogOpen(false)}
        config={cols.config} onToggle={cols.toggle} onMove={cols.move} onReset={cols.reset}
        helpers={disposalCols} title="廃棄履歴の列" testidPrefix="disp-col" />
    </div>
  );
}
